import { ListingStatus } from '@apeiron/common'
import { Planet, PlanetStage } from '@apeiron/planet'
import {
  createStrictContext,
  CustomError,
  ERROR_CODE,
  useAccountContext,
  useMessageContext
} from '@apeiron/temp'
import { RoninVRFDescription, RoninVRFTitle } from '@apeiron/wallet'
import { MORPH_PAGE_STATE } from '@src/constants/morph'
import useMorph from '@src/hooks/contract/useMorph'
import useVerifyPlanet from '@src/hooks/graphql/useVerifyPlanet'
import { Core } from '@src/types/core'
import { isCore } from '@src/util/apeiron/planet'
import { isAfter } from 'date-fns'
import * as R from 'ramda'
import React, {
  Dispatch,
  ReactChild,
  SetStateAction,
  useCallback,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

type IMorphContext = {
  loading: boolean
  core: Core | null
  morphedPlanet: Planet | null
  pageState: MORPH_PAGE_STATE
  setCore: Dispatch<SetStateAction<Core | null>>
  setMorphedPlanet: Dispatch<SetStateAction<Planet | null>>
  setPageState: Dispatch<SetStateAction<MORPH_PAGE_STATE>>
  canMorphCore: (coreToMorph: Core, showError?: boolean) => Promise<boolean>
  morphCore: (coreId: string) => Promise<boolean>
  clean: () => void
}

const [ContextProvider, useMorphContext] =
  createStrictContext<IMorphContext>('Morph')

export { useMorphContext }

export const MorphProvider = (props: Props) => {
  const { children } = props

  const { t } = useTranslation()

  const { verify: verifyPlanet } = useVerifyPlanet()

  const { account } = useAccountContext()

  const doMorph = useMorph()

  const messageContext = useMessageContext()

  const [core, setCore] = useState<Core | null>(null)

  const [morphedPlanet, setMorphedPlanet] = useState<Planet | null>(null)

  const [loading, setLoading] = useState<boolean>(false)

  const [pageState, setPageState] = useState<MORPH_PAGE_STATE>(
    MORPH_PAGE_STATE.IDLE
  )

  const canMorphCore = useCallback(
    async (coreToMorph: Core, showError: boolean = false): Promise<boolean> => {
      const checkIsCore = (): boolean => {
        return R.propOr('', 'stage', coreToMorph) === PlanetStage.Core
      }

      const checkAllowedPeriod = (): boolean => {
        const timeString = R.propOr('', 'bornAt', coreToMorph) as string

        if (!R.isEmpty(timeString)) {
          const nextBornTime = new Date(timeString)

          const now = new Date()

          return isAfter(now, nextBornTime)
        }

        return true
      }

      const checkIsOwner = (): boolean => {
        return (
          R.pathOr('', ['owner', 'walletAddress'], coreToMorph) ===
          account.walletAddress
        )
      }

      const checkIsNotOnSale = (): boolean => {
        return (
          R.propOr(ListingStatus.UNLISTING, 'nftListingStatus', coreToMorph) ===
          ListingStatus.UNLISTING
        )
      }

      if (!checkIsCore()) {
        if (showError) {
          messageContext.setInfo({
            title: t('common.popup.error'),
            message: t('morph.error.not_core'),
            confirm: t('common.popup.ok')
          })
        }

        return false
      }

      if (!checkAllowedPeriod()) {
        if (showError) {
          messageContext.setInfo({
            title: t('common.popup.error'),
            message: t('morph.error.in_cooldown'),
            confirm: t('common.popup.ok')
          })
        }

        return false
      }

      if (!checkIsOwner()) {
        if (showError) {
          messageContext.setInfo({
            title: t('common.popup.error'),
            message: t('morph.error.not_owner'),
            confirm: t('common.popup.ok')
          })
        }

        return false
      }

      if (!checkIsNotOnSale()) {
        if (showError) {
          messageContext.setInfo({
            title: t('common.popup.error'),
            message: t('morph.error.on_sale'),
            confirm: t('common.popup.ok')
          })
        }

        return false
      }

      return true
    },
    [account.walletAddress, messageContext, t]
  )

  const morphCore = useCallback(
    async (coreId: string) => {
      try {
        setLoading(true)

        messageContext.setInfo({
          title: <RoninVRFTitle title={t('wallet.ronin.vrf.morphing')} />,
          message: <RoninVRFDescription />
        })

        setPageState(MORPH_PAGE_STATE.MORPHING)

        const planetId = await doMorph.fetch({
          coreId
        })

        const success = await verifyPlanet(
          planetId,
          (planet: Planet) => !isCore(planet)
        )

        if (!success) {
          throw new CustomError(
            'Cannot verify morphed planet',
            ERROR_CODE.CONTRACT.WAIT_SEND_OPERATION_RESULT_TIMEOUT
          )
        }

        messageContext.setInfo(null)

        setPageState(MORPH_PAGE_STATE.MORPH_SUCCESS)

        setLoading(false)

        return true
      } catch (e) {
        const error = e as CustomError

        messageContext.setInfo({
          title: t('common.popup.error'),
          message: error.message,
          confirm: t('common.popup.ok'),
          onClose: () => {}
        })
      }

      return false
    },
    [messageContext, t, doMorph, verifyPlanet]
  )

  const clean = useCallback(() => {
    setCore(null)
    setMorphedPlanet(null)
    setPageState(MORPH_PAGE_STATE.IDLE)
  }, [])

  return (
    <ContextProvider
      value={{
        loading,
        core,
        morphedPlanet,
        pageState,
        setCore,
        setMorphedPlanet,
        setPageState,
        canMorphCore,
        morphCore,
        clean
      }}
    >
      {children}
    </ContextProvider>
  )
}

type Props = {
  children: ReactChild | ReactChild[]
}
